import type { IntlShape } from 'react-intl';

import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';

import { tableI18n } from './table-formatters.i18n';

/** Returns a formatter that replaces null/undefined/empty with "N/A". */
export const createNaFallback =
    (intl: IntlShape) =>
    (value: DataTableCellValue): string => {
        if (value === null || value === undefined || value === '') {
            return intl.formatMessage(tableI18n.notAvailable);
        }
        return String(value);
    };

/** Returns a formatter that maps A/I/X state codes to labels. */
export const createStateLabel =
    (intl: IntlShape) =>
    (value: DataTableCellValue): string => {
        if (value === 'A') return intl.formatMessage(tableI18n.active);
        if (value === 'I') return intl.formatMessage(tableI18n.inactive);
        if (value === 'X') return intl.formatMessage(tableI18n.archived);
        return intl.formatMessage(tableI18n.notAvailable);
    };

/** Standard filter options for the A/I/X state column. */
export const createStateFilterOptions = (intl: IntlShape) => [
    { label: intl.formatMessage(tableI18n.active), value: 'A' },
    { label: intl.formatMessage(tableI18n.inactive), value: 'I' },
    { label: intl.formatMessage(tableI18n.archived), value: 'X' },
];

/** Returns a formatter that displays ISO datetime strings in locale-appropriate format. */
export const createDateTimeFormatter = (intl: IntlShape) => {
    const naFallback = createNaFallback(intl);
    return (value: DataTableCellValue): string => {
        if (value === null || value === undefined || value === '') return naFallback(value);
        const date = new Date(String(value));
        if (Number.isNaN(date.getTime())) return naFallback(null);
        return intl.formatDate(date, { dateStyle: 'medium', timeStyle: 'short' });
    };
};

/** Returns a formatter for workspace access levels (private/public). */
export const createAccessLabel = (intl: IntlShape) => {
    const naFallback = createNaFallback(intl);
    return (value: DataTableCellValue): string => {
        if (value === 'public') return intl.formatMessage(tableI18n.accessPublic);
        if (value === 'private') return intl.formatMessage(tableI18n.accessPrivate);
        return naFallback(value);
    };
};

/** Standard filter options for workspace access levels. */
export const createAccessFilterOptions = (intl: IntlShape) => [
    { label: intl.formatMessage(tableI18n.accessPublic), value: 'public' },
    { label: intl.formatMessage(tableI18n.accessPrivate), value: 'private' },
];
